import { useMemo } from 'react';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ContactForm from '@/components/feature/ContactForm';
import SectionHeading from '@/components/feature/SectionHeading';
import SiteLayout from '@/components/feature/SiteLayout';
import { useSeo } from '@/hooks/useSeo';
import { contactChannels, siteConfig, testimonials } from '@/mocks/site';

const principles = [
  {
    icon: 'ri-shield-check-line',
    title: '匿名优先',
    desc: '所有讲述与线索默认做匿名化处理，不公布姓名、住址与可识别的个人特征。',
  },
  {
    icon: 'ri-file-search-line',
    title: '可核实再发布',
    desc: '优先采用公开报道与办案通报中交叉出现的信息，避免把孤例当成普遍规律。',
  },
  {
    icon: 'ri-user-heart-line',
    title: '不二次伤害',
    desc: '不渲染猎奇细节，不复述可能刺激当事人的内容，把笔墨放在识别与防范上。',
  },
  {
    icon: 'ri-refresh-line',
    title: '持续修订',
    desc: '骗局手法在变化，档案也会随之更新；发现过时或被误解的内容，我们及时更正。',
  },
];

export default function About() {
  const jsonLd = useMemo(
    () => [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: `关于我们｜${siteConfig.fullName}`,
        inLanguage: 'zh-CN',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: `联系我们｜${siteConfig.fullName}`,
        inLanguage: 'zh-CN',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: siteConfig.fullName,
        email: siteConfig.email,
        description: siteConfig.intro,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://readdy.ai/' },
          { '@type': 'ListItem', position: 2, name: '关于我们' },
        ],
      },
    ],
    [],
  );

  useSeo({
    title: '关于我们｜缅北黑料 · 反诈内幕档案库',
    description:
      '了解缅北黑料反诈内幕档案库的定位、内容原则与联系方式。我们专注整理可核实的跨境诈骗线索，帮助用户认清骗局套路、远离跨境陷阱。',
    keywords: '关于缅北黑料,缅北黑料反诈档案,线索投稿,跨境诈骗科普',
    path: '/about',
    jsonLd,
  });

  return (
    <SiteLayout>
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-content px-4 py-10 md:px-6 md:py-16">
          <Breadcrumbs items={[{ label: '关于我们' }]} />
          <h1 className="mt-5 max-w-3xl font-heading text-3xl font-black leading-snug text-foreground-950 md:text-4xl">
            关于我们：把看不懂的内幕，写成用得上的档案
          </h1>
          <p className="mt-5 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">{siteConfig.intro}</p>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="mx-auto grid w-full max-w-content grid-cols-1 gap-10 px-4 py-14 md:px-6 md:py-20 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-5">
            <div className="h-72 w-full overflow-hidden rounded-lg md:h-[440px]">
              <img
                src="https://readdy.ai/api/search-image?query=a%20stack%20of%20worn%20cardboard%20case%20files%20tied%20with%20string%20on%20a%20dark%20table%2C%20warm%20side%20lighting%2C%20muted%20brown%20and%20amber%20documentary%20tones%2C%20cinematic%2C%20high%20detail&width=1000&height=1200&seq=mb-about-01&orientation=portrait"
                alt="整理成册的跨境诈骗线索档案"
                title="缅北黑料反诈内幕档案库品牌介绍"
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <SectionHeading eyebrow="网站定位" title="我们只做一件事：让骗局无处藏身" />
            <div className="mt-6 space-y-4 text-sm leading-[1.9] text-foreground-700 md:text-base">
              <p>
                {siteConfig.name}成立于 {siteConfig.foundedYear} 年，最初是几位反诈志愿者整理的共享文档。随着留言和投稿越来越多，我们把零散的线索整理成有结构、可检索的档案库，方便任何人快速查到某一类骗局的关键特征。
              </p>
              <p>
                我们不制造恐慌，也不消费苦难。每一篇档案都围绕三个问题展开：这类骗局如何让人上钩、哪些环节是可以被识破的、当事人和家属此刻应该做什么。
              </p>
              <p>
                如果你正在犹豫一份境外工作机会，请先看完「防骗指南」分类；如果你有具体经历愿意分享，欢迎通过下方表单提交线索。
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100">
        <div className="mx-auto w-full max-w-content px-4 py-14 md:px-6 md:py-20">
          <SectionHeading
            eyebrow="内容原则"
            title="我们如何对待每一条线索"
            desc="反诈内容的底线是真实与克制。以下四条是我们编辑时的硬性标准。"
          />
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {principles.map((p) => (
              <div key={p.title} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className={`${p.icon} text-xl`} aria-hidden="true"></i>
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="mx-auto w-full max-w-content px-4 py-14 md:px-6 md:py-20">
          <SectionHeading eyebrow="读者反馈" title="他们的声音" align="center" />
          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
            {testimonials.map((t) => (
              <figure key={t.name} className="flex flex-col rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
                <i className="ri-double-quotes-l text-2xl text-primary-400" aria-hidden="true"></i>
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">{t.quote}</blockquote>
                <figcaption className="mt-4 border-t border-background-200 pt-4 text-sm">
                  <strong className="font-semibold text-foreground-900">{t.name}</strong>
                  <span className="ml-2 text-xs text-foreground-500">{t.role}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-100">
        <div className="mx-auto grid w-full max-w-content grid-cols-1 gap-10 px-4 py-14 md:px-6 md:py-20 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="联系我们"
              title="有线索，或者只是想说说话"
              desc="无论是招聘线索、被骗经历，还是希望我们核对一条信息，都可以在这里留言。我们尽量在收到后尽快回复。"
            />
            <ul className="mt-8 space-y-4">
              {contactChannels.map((ch) => (
                <li key={ch.label} className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                    <i className={`${ch.icon} text-lg`} aria-hidden="true"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">{ch.label}</p>
                    <p className="mt-0.5 text-sm font-medium text-foreground-900">{ch.value}</p>
                  </div>
                </li>
              ))}
            </ul>
            <p className="mt-6 flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
              <i className="ri-alert-line mt-0.5" aria-hidden="true"></i>
              <span>本站不提供付费帮助，也不推荐任何中介机构。凡以"付费捞人"为名索要钱财的，都请提高警惕。</span>
            </p>
          </div>
          <div className="lg:col-span-7">
            <ContactForm />
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}